import React, { useEffect, useState } from "react";
import { Gift, Link2, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { REFERRAL_MSG, waHref, redditHref, copyText } from "@/lib/share";

// Global referral popup. Opens when an upload is blocked by the free limit
// (the API interceptor dispatches "loom:referral-needed"), offering the user a
// personal link that grants 3 bonus uploads to both sides.
export default function ReferralModal() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onNeed = async () => {
      setOpen(true);
      if (!data) {
        try { const r = await api.get("/referral"); setData(r.data); }
        catch { /* silent — modal still shows the ask */ }
      }
    };
    window.addEventListener("loom:referral-needed", onNeed);
    return () => window.removeEventListener("loom:referral-needed", onNeed);
  }, [data]);

  if (!open) return null;

  const url = data?.url || "";
  const openSocial = (href) => window.open(href, "_blank", "noopener,noreferrer");

  const doCopy = async () => {
    if (!url) return;
    const ok = await copyText(url);
    if (ok) { setCopied(true); toast.success("Referral link copied"); setTimeout(() => setCopied(false), 1800); }
  };

  const discordShare = async () => {
    if (!url) return;
    const ok = await copyText(REFERRAL_MSG(url));
    if (ok) toast.success("Message copied — paste it into Discord");
    window.open("https://discord.com/channels/@me", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-stone-900/40 backdrop-blur-sm" data-testid="referral-modal">
      <div className="sticky-card p-7 w-full max-w-md relative">
        <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-stone-400 hover:text-stone-700" data-testid="referral-modal-close">
          <X className="w-5 h-5" />
        </button>
        <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
          <Gift className="w-6 h-6 text-amber-600" />
        </div>
        <h2 className="font-display text-3xl mt-5 leading-tight">Need more free uploads?</h2>
        <p className="text-stone-600 mt-3 leading-relaxed">
          Share LoomPDF with your class group chat! Copy your link below — when a classmate
          tries it, <span className="marker">you both get 3 bonus uploads</span>.
        </p>

        {data && (
          <p className="text-xs uppercase tracking-[0.18em] text-stone-500 mt-5" data-testid="referral-stats">
            {data.bonus_uploads} bonus uploads earned · {data.referred_count} friends joined
          </p>
        )}

        <div className="mt-3 flex items-center gap-2">
          <input readOnly value={url || "Loading your link…"} className="input text-xs flex-1"
                 data-testid="referral-link-input" onFocus={(e) => e.target.select()} />
          <button onClick={doCopy} className="btn-primary px-3 py-2 inline-flex items-center gap-1 text-sm" data-testid="referral-copy-button">
            {!url ? <Loader2 className="w-4 h-4 animate-spin" /> : copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
          </button>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <button onClick={() => url && openSocial(waHref(REFERRAL_MSG(url)))}
            className="rounded-xl border border-stone-200 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
            data-testid="referral-whatsapp-button">WhatsApp</button>
          <button onClick={discordShare}
            className="rounded-xl border border-stone-200 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 transition-colors"
            data-testid="referral-discord-button">Discord</button>
          <button onClick={() => url && openSocial(redditHref(url, "Turn your class PDFs into study decks with LoomPDF"))}
            className="rounded-xl border border-stone-200 py-2 text-sm font-medium text-orange-700 hover:bg-orange-50 transition-colors"
            data-testid="referral-reddit-button">Reddit</button>
        </div>

        <button onClick={() => { setOpen(false); window.location.href = "/pricing"; }}
          className="btn-ghost w-full mt-5 text-sm" data-testid="referral-upgrade-link">
          …or upgrade to Pro for 50 decks
        </button>
      </div>
    </div>
  );
}
